import { daysInMonth, localDateString, mondayIndex, startOfMonth } from "./dateUtils";

// Takvim hücresinin durumu. Sunucu ayın HER günü için bir kayıt döndürüyor
// (hasLog=false olanlar dahil, dayTotals sıfırlarla dolu). Gece açılan 0
// değerli kayıt da girilmiş sayılmaz: "girildi mi" sorusu hasEntry'ye sorulur
// (alanı olmayan eski kayıt girilmiş sayılır).
export const DAY_STATUS = {
  PERFECT: "perfect", // tüm hedefler tamam → yıldız
  PARTIAL: "partial", // kısmi ilerleme
  ZERO: "zero", // kayıt açılmış ama ilerleme yok
  MISSED: "missed", // geçmiş gün, girilmemiş
  TODAY_EMPTY: "todayEmpty", // bugün, henüz girilmemiş
  FUTURE: "future", // gelecek gün
};

function statusFor({ isFuture, isToday, hasLog, percent, isPerfect }) {
  if (isFuture) return DAY_STATUS.FUTURE;
  if (!hasLog) return isToday ? DAY_STATUS.TODAY_EMPTY : DAY_STATUS.MISSED;
  if (isPerfect) return DAY_STATUS.PERFECT;
  if (percent > 0) return DAY_STATUS.PARTIAL;
  return DAY_STATUS.ZERO;
}

/**
 * Ayın günlerini takvim ızgarası için hazırlar.
 * Zincir (chainLeft/chainRight) yalnızca ardışık mükemmel günler arasında kurulur
 * ve satır başında/sonunda kesilir — bağlayıcı çizgi ızgara boşluğuna çizildiği için
 * satır atlayan bir bağlantı görsel olarak anlamsız olurdu.
 */
export function buildMonthDays({
  monthDate,
  dailyLogs = [],
  today = new Date(),
  selectedDate = null,
  hatimDates = null,
}) {
  const first = startOfMonth(monthDate);
  const offset = mondayIndex(first);
  const total = daysInMonth(monthDate);
  const todayStr = localDateString(today);

  const byDate = new Map((dailyLogs || []).map((entry) => [entry.date, entry]));

  const days = [];
  for (let day = 1; day <= total; day++) {
    const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
    const dateStr = localDateString(date);
    const entry = byDate.get(dateStr) || null;
    const totals = entry?.dayTotals || null;

    // Girilmiş gün: kayıt var ve hasEntry false değil (eski kayıtta alan yok).
    const hasLog =
      Boolean(entry?.hasLog) && entry?.hasEntry !== false && totals?.hasEntry !== false;
    // Gün puanı sunucudan (dayScore), eski sunucuda saklanan ortalama.
    const score = Number(entry?.dayScore ?? totals?.avgCompletion) || 0;
    const percent = hasLog ? Math.max(0, Math.min(100, Math.round(score))) : 0;
    const isPerfect = hasLog && Boolean(totals?.isPerfectDay);

    days.push({
      date: dateStr,
      day,
      columnIndex: (offset + day - 1) % 7,
      isToday: dateStr === todayStr,
      isFuture: dateStr > todayStr,
      isSelected: dateStr === selectedDate,
      hasLog,
      percent,
      isPerfect,
      streak: isPerfect ? Number(totals?.leftStreak || 0) : 0,
      // Kayıt yoksa o gün için hedef de kaydedilmemiştir; rozet basmıyoruz.
      goalCount: hasLog ? Number(totals?.goalCount || 0) : 0,
      hasHatim: Boolean(hatimDates && hatimDates.has(dateStr)),
      successCount: Number(totals?.successCount || 0),
      eventCount: Number(entry?.eventCount || 0),
      status: statusFor({
        isFuture: dateStr > todayStr,
        isToday: dateStr === todayStr,
        hasLog,
        percent,
        isPerfect,
      }),
      chainLeft: false,
      chainRight: false,
    });
  }

  for (let i = 0; i < days.length; i++) {
    const current = days[i];
    if (!current.isPerfect) continue;
    const previous = days[i - 1];
    const next = days[i + 1];
    if (previous?.isPerfect && current.columnIndex !== 0) current.chainLeft = true;
    if (next?.isPerfect && current.columnIndex !== 6) current.chainRight = true;
  }

  return { offset, days };
}

/**
 * Hatim instance'larının kapsadığı günlerin kümesi.
 * startDate ve endDate dahil; takvimde o günlere hatim rozeti basmak için.
 */
export function hatimDateSet(instances = []) {
  const set = new Set();
  for (const inst of instances || []) {
    const start = inst?.startDate ? new Date(inst.startDate) : null;
    const end = inst?.endDate ? new Date(inst.endDate) : null;
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) continue;
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());
    // Aşırı uzun aralıklarda döngüyü sınırla (bozuk veri koruması).
    let guard = 0;
    while (cursor <= last && guard < 800) {
      set.add(localDateString(cursor));
      cursor.setDate(cursor.getDate() + 1);
      guard += 1;
    }
  }
  return set;
}

/** Ay içindeki en uzun mükemmel gün zinciri. */
export function longestChain(days = []) {
  let best = 0;
  let run = 0;
  for (const d of days) {
    run = d.isPerfect ? run + 1 : 0;
    if (run > best) best = run;
  }
  return best;
}

/** Ayın özeti: yıldız sayısı, ortalama gün puanı ve sayısı (yalnız girilmiş günler). */
export function monthSummary(days = []) {
  const logged = days.filter((d) => d.hasLog);
  const stars = days.filter((d) => d.isPerfect).length;
  const average = logged.length
    ? Math.round(logged.reduce((sum, d) => sum + d.percent, 0) / logged.length)
    : 0;
  return { stars, average, loggedCount: logged.length, longestChain: longestChain(days) };
}
